import { FC } from 'react'
import { Typography } from '@mui/material'

/* Utils */
import { CompetitorAnalysisPerformanceMetric } from 'models'

/* Components */
import { getSlaUnit } from 'ui/pages/library/utils'
import { AnalysisItem, AnalysisItemDataContainer } from '../competitorAnalysis.styles'
import CompetitorAnalysisSlider from './CompetitorAnalysisSlider'

export interface Mark {
  label: string
  value: number
}

interface Props {
  item: CompetitorAnalysisPerformanceMetric
  sla?: number
  unit: string
  active: boolean
  name: string
}

const CompetitorAnalysisItem: FC<Props> = ({ item, active, sla, unit }) => {
  const measurementUnit = getSlaUnit(unit)

  const getMarks = (): Mark[] => {
    const rangeHigh = item.summary.performance_range.high

    // An SLA past the industry range becomes the axis end itself, otherwise its marker falls off the track
    if (typeof sla !== 'undefined' && sla > rangeHigh) {
      return Array.from({ length: 6 }, (_, i) => {
        const value = (sla * i) / 5

        return {
          value,
          // An SLA that doesn't divide by 5 leaves the ticks fractional, so they round for display only
          label: `${Number(value.toFixed(1))}${measurementUnit}`
        }
      })
    }

    // The axis always starts at 0, so the step has to be rounded up off `high` to keep the data inside the track.
    const step = Math.max(1, Math.ceil(rangeHigh / 5))

    return Array.from({ length: 6 }, (_, i) => ({
      value: i * step,
      label: `${i * step}${measurementUnit}`
    }))
  }

  const marks = getMarks()
  const max = marks[marks.length - 1].value

  const toPercent = (value: number): number => (value * 100) / max

  const slaPosition = typeof sla !== 'undefined' ? toPercent(sla) : 0
  const avgIndustryPosition = toPercent(item.summary.industry_performance_avg)

  const topRangeLeft = toPercent(item.summary.top_performance_range.low)
  const topRangeWidth = toPercent(item.summary.top_performance_range.high) - topRangeLeft

  const lowRangeLeft = toPercent(item.summary.bottom_performance_range.low)
  const lowRangeWidth = toPercent(item.summary.bottom_performance_range.high) - lowRangeLeft

  const barLeft = Math.min(topRangeLeft, lowRangeLeft)
  const barWidth = Math.max(topRangeLeft + topRangeWidth, lowRangeLeft + lowRangeWidth) - barLeft

  const isReverse = item.summary.bottom_performance_range.high < item.summary.top_performance_range.high

  return (
    <AnalysisItem key={item.industry_name}>
      <Typography className="analysis-category-text">{item.industry_name}</Typography>
      <AnalysisItemDataContainer>
        <CompetitorAnalysisSlider
          sla={sla}
          active={active}
          item={item}
          measurementUnit={measurementUnit}
          unit={unit}
          slaPosition={slaPosition}
          avgIndustryPosition={avgIndustryPosition}
          marks={marks}
          lowRangeLeft={lowRangeLeft}
          lowRangeWidth={lowRangeWidth}
          topRangeLeft={topRangeLeft}
          topRangeWidth={topRangeWidth}
          barWidth={barWidth}
          barLeft={barLeft}
          isReverse={isReverse}
          max={max}
        />
      </AnalysisItemDataContainer>
    </AnalysisItem>
  )
}

export default CompetitorAnalysisItem
